import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  addDaysToDateKey,
  buildDateKeySequence,
  clampDateKey,
  compareDateKeys,
  getBookingWindowLimits,
  normalizeDateKey,
} from '../utils/golfTime'

const VISIBLE_DAYS = 8

export default function GolfDateCarousel({
  bookingWindow,
  timeZone,
  selectedDateKey,
  onSelectDate,
  formatPrimary,
  formatSecondary,
}) {
  const { t: memberText } = useOptionalPrivanaI18n()
  const bookingLimits = useMemo(
    () => getBookingWindowLimits(bookingWindow, timeZone),
    [bookingWindow, timeZone]
  )

  const todayKey = bookingLimits.todayKey
  const maxDateKey = bookingLimits.maxDateKey

  const clampedSelectedDate = useMemo(() => {
    if (!todayKey) return selectedDateKey || null
    return clampDateKey(selectedDateKey || todayKey, todayKey, maxDateKey)
  }, [maxDateKey, selectedDateKey, todayKey])

  useEffect(() => {
    if (!todayKey) return
    const next = clampedSelectedDate || todayKey
    if (next && next !== selectedDateKey && typeof onSelectDate === 'function') {
      onSelectDate(next)
    }
  }, [clampedSelectedDate, onSelectDate, selectedDateKey, todayKey])

  const [visibleStartDateKey, setVisibleStartDateKey] = useState(todayKey || selectedDateKey || null)

  useEffect(() => {
    if (!todayKey) return
    setVisibleStartDateKey((current) => {
      const base = current && compareDateKeys(current, todayKey) >= 0 ? current : todayKey
      const target = clampedSelectedDate || todayKey
      let nextStart = base || todayKey
      const currentWindowEnd = nextStart ? addDaysToDateKey(nextStart, VISIBLE_DAYS - 1) : null

      if (currentWindowEnd && compareDateKeys(target, currentWindowEnd) > 0) {
        nextStart = addDaysToDateKey(target, -(VISIBLE_DAYS - 1))
      } else if (compareDateKeys(target, nextStart) < 0) {
        nextStart = target
      }

      const latestStart = maxDateKey ? addDaysToDateKey(maxDateKey, -(VISIBLE_DAYS - 1)) : null
      const clampedStart = clampDateKey(nextStart, todayKey, latestStart)
      return clampedStart || todayKey
    })
  }, [clampedSelectedDate, maxDateKey, todayKey])

  const visibleDates = useMemo(
    () => (visibleStartDateKey ? buildDateKeySequence(visibleStartDateKey, VISIBLE_DAYS) : []),
    [visibleStartDateKey]
  )

  const lastVisibleDate = useMemo(
    () => (visibleStartDateKey ? addDaysToDateKey(visibleStartDateKey, VISIBLE_DAYS - 1) : null),
    [visibleStartDateKey]
  )

  const canMoveLeft =
    Boolean(todayKey) && Boolean(visibleStartDateKey) && compareDateKeys(visibleStartDateKey, todayKey) > 0
  const canMoveRight = maxDateKey
    ? lastVisibleDate && compareDateKeys(lastVisibleDate, maxDateKey) < 0
    : Boolean(visibleStartDateKey)

  const moveWindow = (delta) => {
    if (!visibleStartDateKey || !todayKey) return
    const tentativeStart = addDaysToDateKey(visibleStartDateKey, delta)
    const latestStart = maxDateKey ? addDaysToDateKey(maxDateKey, -(VISIBLE_DAYS - 1)) : null
    const clampedStart = clampDateKey(tentativeStart, todayKey, latestStart)
    setVisibleStartDateKey(clampedStart || todayKey)
  }

  const isDateSelectable = (dateKey) => {
    const normalized = normalizeDateKey(dateKey)
    if (!normalized || !todayKey) return false
    if (compareDateKeys(normalized, todayKey) < 0) return false
    if (maxDateKey && compareDateKeys(normalized, maxDateKey) > 0) return false
    return true
  }

  const resolvePrimaryLabel = (dateKey) => {
    if (typeof formatPrimary === 'function') return formatPrimary(dateKey)
    return dateKey
  }

  const resolveSecondaryLabel = (dateKey) => {
    if (typeof formatSecondary === 'function') return formatSecondary(dateKey)
    return ''
  }

  return (
    <div className="mp-golf-date-scroller" role="tablist" aria-label={memberText("portalPresentation.member_select_date")}>
      <button
        type="button"
        className="mp-golf-date-chevron"
        aria-label={memberText("golf.teeSheet.previousDay")}
        onClick={() => moveWindow(-1)}
        disabled={!canMoveLeft}
      >
        <ChevronLeft size={18} />
      </button>

      <div className="mp-golf-day-row">
        {visibleDates.map((dateKey) => {
          const normalized = normalizeDateKey(dateKey)
          if (!normalized) return null
          const isActive = normalized === clampedSelectedDate
          const selectable = isDateSelectable(normalized)

          return (
            <button
              key={normalized}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`mp-golf-day ${isActive ? 'active' : ''} ${!selectable ? 'disabled' : ''}`}
              onClick={() => selectable && onSelectDate?.(normalized)}
              disabled={!selectable}
            >
              <span className="mp-golf-day__label">{resolvePrimaryLabel(normalized)}</span>
              <span className="mp-golf-day__date">{resolveSecondaryLabel(normalized)}</span>
            </button>
          )
        })}
      </div>

      <button
        type="button"
        className="mp-golf-date-chevron"
        aria-label={memberText("golf.teeSheet.nextDay")}
        onClick={() => moveWindow(1)}
        disabled={!canMoveRight}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  )
}
